import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, merge, Observable } from 'rxjs';
import { filter, map, shareReplay, skip, startWith } from 'rxjs/operators';
import { BreadcrumbItem } from '../models/breadcrumb.model';
import { buildBreadcrumbs, findRouteRoles, resolveServedUrl, stripQueryString } from '../utils/breadcrumb.util';
import { UserRole } from '../enums/global.enum';
// imported by path, not through the services barrel, to keep this out of an import cycle.
import { UserRoleService } from './user-role.service';

/** The owning record's id, together with the path of the page that published it. */
interface ParentRecord {
  /** Path only: a query param change keeps the user on the same record, so it keeps the id. */
  readonly path: string;
  readonly id: string | number | null;
}

/**
 * Derives the breadcrumb from the router. Nothing is persisted and nothing accumulates across
 * navigations: the only state is the owning record's id, and it is stamped with the path it was
 * published for, so it can never leak from the page that set it into the next one.
 */
@Injectable({
  providedIn: 'root',
})
export class BreadcrumbService {
  readonly breadcrumbs$: Observable<BreadcrumbItem[]>;

  /** Owning record's id for the page that published it. Read back only on that same path. */
  private readonly parent$ = new BehaviorSubject<ParentRecord>({ path: '', id: null });

  constructor(
    private router: Router,
    private translateService: TranslateService,
    private userRoleService: UserRoleService
  ) {
    const navigation$ = this.router.events.pipe(filter((event) => event instanceof NavigationEnd));

    // `onLangChange` alone misses the first load: the locale file is fetched by `translate.use()`
    // outside any APP_INITIALIZER, so a deep link can build before any translation exists and
    // would otherwise keep rendering raw `BreadCrumb.*` keys.
    const translations$ = merge(
      this.translateService.onLangChange,
      this.translateService.onTranslationChange,
      this.translateService.onDefaultLangChange
    );

    this.breadcrumbs$ = merge(
      navigation$,
      translations$,
      // its current value is already covered by `startWith`, so only later ids are a new trigger.
      this.parent$.pipe(skip(1))
    ).pipe(
      // the first subscriber is usually a page that the router has already navigated to.
      startWith(null),
      map(() => this.build()),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  /**
   * Publishes the owning record's id for the page currently open, e.g. a truck's provider.
   *
   * Called by pages whose route declares a `parent` it cannot carry in the url. Until this lands
   * the parent level renders as plain text, so calling it late costs a link, never a wrong one.
   */
  setParentId(id: string | number | null | undefined): void {
    this.parent$.next({ path: stripQueryString(this.router.url), id: id ?? null });
  }

  /**
   * The owning record's id, but only while the page that published it is still the page on screen.
   *
   * Stamping the id with its path is what makes a stale owner impossible, rather than clearing it
   * on navigation: the breadcrumb stream is ref-counted and most pages hide the breadcrumb
   * entirely, so any reset driven by a subscriber would simply not run while it is hidden - and the
   * id left behind would then be replayed onto the next page, linking its parent to the wrong record.
   */
  private currentParentId(): string | number | null {
    const parent = this.parent$.value;

    return parent.path === stripQueryString(this.router.url) ? parent.id : null;
  }

  private build(): BreadcrumbItem[] {
    return buildBreadcrumbs(
      this.router.routerState.snapshot.root,
      this.router.url,
      (key) => this.translateService.instant(key),
      this.currentParentId(),
      (url) => this.isReachable(url)
    );
  }

  /**
   * A level is a link only if the router serves the url and the user's roles get them onto it.
   *
   * Access is checked against the url the router ends up on, not the one the level points at:
   * for a level whose route only redirects, the guard that decides the outcome sits on the
   * target, and checking the redirect itself would always come back unguarded.
   */
  private isReachable(url: string): boolean {
    const served = resolveServedUrl(this.router.config, url);

    return served !== undefined && this.canAccess(served);
  }

  /**
   * Whether `PermissionGuard` would let the user onto `url`.
   *
   * A route that declares no roles carries no permission guard, and a route that cannot be found
   * is not evidence of a denial, so both count as accessible - only a role list the user fails
   * hides a level.
   */
  private canAccess(url: string): boolean {
    const roles = findRouteRoles(this.router.config, url) as UserRole[] | undefined;

    return !roles?.length || this.userRoleService.isUserHasRoles(roles);
  }
}
